import { useState } from 'react';
import type { InvestorListDTO, InvoiceTransactionDraftDTO } from '@inversor-hub/types';
import {
  useConfirmInvoiceTransactionDraft,
  useRejectInvoiceTransactionDraft,
  useUpdateInvoiceTransactionDraft,
} from '@/lib/api/investment-transactions';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';
import type { useAnalytics } from '@/hooks/useAnalytics';
import type useSnackbar from '@/hooks/useSnackbar';
import type { ConfLevel, DraftFormState } from './types';
import { investmentLabel, toConfLevel, toFormState } from './utils';

interface UseInvoiceDraftReviewParams {
  draft: InvoiceTransactionDraftDTO;
  investments?: InvestorListDTO[];
  onConfirmed: (investmentName: string | null) => void;
  onDiscarded: () => void;
  trackEvent: ReturnType<typeof useAnalytics>['trackEvent'];
  show: ReturnType<typeof useSnackbar>['show'];
}

export const useInvoiceDraftReview = ({
  draft,
  investments,
  onConfirmed,
  onDiscarded,
  trackEvent,
  show,
}: UseInvoiceDraftReviewParams) => {
  const updateDraft = useUpdateInvoiceTransactionDraft();
  const confirmDraft = useConfirmInvoiceTransactionDraft();
  const rejectDraft = useRejectInvoiceTransactionDraft();

  const [form, setForm] = useState<DraftFormState>(() => toFormState(draft));
  const [edited, setEdited] = useState<Partial<Record<keyof DraftFormState, boolean>>>({});

  const setField = <K extends keyof DraftFormState>(key: K, value: DraftFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setEdited((prev) => ({ ...prev, [key]: true }));
  };

  const handleAmountChange = (cents: number | null) => {
    setForm((prev) => ({ ...prev, amount: cents != null ? String(cents) : '' }));
    setEdited((prev) => ({ ...prev, amount: cents !== (draft.amount ?? null) }));
  };

  const conf = {
    amount: toConfLevel(draft.extractionConfidence),
    date: toConfLevel(draft.extractionConfidence),
    category: toConfLevel(draft.extractionConfidence),
    investment: toConfLevel(draft.matchConfidence),
  } satisfies Record<string, ConfLevel>;

  const lowUnresolved =
    (conf.amount === 'low' && !edited.amount) ||
    (conf.date === 'low' && !edited.date) ||
    (conf.category === 'low' && !edited.category) ||
    (conf.investment === 'low' && !edited.investmentId);

  const canConfirm = !!form.investmentId && !!form.amount && !!form.date && !!form.category;
  const selectedInvestment = investments?.find((investment) => investment.id === form.investmentId);
  const investmentName = selectedInvestment ? investmentLabel(selectedInvestment) : null;
  const isBusy = updateDraft.isPending || confirmDraft.isPending || rejectDraft.isPending;

  const confirm = async () => {
    if (!canConfirm) return;

    try {
      await updateDraft.mutateAsync({
        draftId: draft.id,
        data: {
          investmentId: form.investmentId || null,
          type: form.type,
          category: form.category,
          concept: form.concept || null,
          amount: form.amount ? Number(form.amount) : null,
          date: form.date || null,
          paymentMethod: form.paymentMethod || null,
        },
      });
      await confirmDraft.mutateAsync(draft.id);
      trackProductEvent(trackEvent, 'invoice_draft_updated', {
        draft_id: draft.id,
        investment_id: form.investmentId,
        platform: ANALYTICS_PLATFORM,
      });
      trackProductEvent(trackEvent, 'invoice_draft_confirmed', {
        draft_id: draft.id,
        platform: ANALYTICS_PLATFORM,
      });
      onConfirmed(investmentName);
    } catch {
      show({ title: 'No se pudo confirmar la factura', variant: 'error' });
    }
  };

  const discard = async () => {
    try {
      await rejectDraft.mutateAsync({ draftId: draft.id, reason: 'Rechazada por el usuario' });
      trackProductEvent(trackEvent, 'invoice_draft_rejected', {
        draft_id: draft.id,
        platform: ANALYTICS_PLATFORM,
      });
      onDiscarded();
    } catch {
      show({ title: 'No se pudo descartar la factura', variant: 'error' });
    }
  };

  return {
    state: {
      form,
      edited,
      conf,
      lowUnresolved,
      canConfirm,
      isBusy,
    },
    actions: {
      setField,
      handleAmountChange,
      confirm,
      discard,
    },
    meta: {
      investmentName,
      investmentLabel,
    },
  };
};

export type InvoiceDraftReviewController = ReturnType<typeof useInvoiceDraftReview>;
